import React, { forwardRef, useEffect, useMemo, useRef, useState } from "react";
import {
	FormControl,
	Autocomplete,
	TextField,
	MenuItem,
	Box,
} from "@mui/material";
import { Controller, useFormContext } from "react-hook-form";
import { CustomAutoCompleteProps } from "./CustomAutoComplete.Props";
import { IOption } from "utility/interfaces/select-option";
import { CustomTooltip } from "components/CustomTooltip";

const CustomListbox = forwardRef<
	HTMLUListElement,
	React.HTMLAttributes<HTMLUListElement>
>(function CustomListbox(props, ref) {
	return (
		<ul
			{...props}
			ref={ref}
			style={{
				maxHeight: 300,
				overflow: "auto",
				...props.style,
			}}
		/>
	);
});

export const CustomAutoComplete: React.FC<CustomAutoCompleteProps> = ({
	name,
	options,
	label,
	isDisabled,
	hideErrorMessage = false,
	className = "",
	onBeforeChange,
	handleChange,
	startAdornment,
	endAdornment,
	largeList = false,
	isShowDescription = false,
	PaperComponent,
	renderOption,
}) => {
	const methods = useFormContext();
	const [value, setValue] = useState<IOption | null>(null);
	const selectedValue = methods.watch(name);
	useEffect(() => {
		const item = options.find((opt) => opt.value === selectedValue);
		setValue(item ? item : null);
	}, [selectedValue, options]);

	const [inputValue, setInputValue] = useState("");
	const [visibleCount, setVisibleCount] = useState(100);
	const [filteredOptions, setFilteredOptions] = useState<any[]>([]);
	const listboxRef = useRef<HTMLUListElement | null>(null);

	const getLabel = (opt: IOption) => opt?.label ?? "";

	// Debounced filter logic for large lists
	useEffect(() => {
		if (!largeList) {
			setFilteredOptions(options);
			return;
		}

		const delay = setTimeout(() => {
			const lower = inputValue.toLowerCase();
			let filtered = options;

			if (inputValue) {
				filtered = options.filter((opt) =>
					getLabel(opt).toLowerCase().includes(lower)
				);
			}

			setFilteredOptions(filtered.slice(0, visibleCount));
		}, 250);

		return () => clearTimeout(delay);
	}, [inputValue, options, largeList, visibleCount]);

	// Scroll event for infinite loading
	useEffect(() => {
		const listbox = listboxRef.current;
		if (!listbox || !largeList) return;

		const handleScroll = () => {
			const { scrollTop, scrollHeight, clientHeight } = listbox;
			if (scrollHeight - scrollTop - clientHeight < 50) {
				// near bottom
				setVisibleCount((prev) => Math.min(prev + 100, options.length));
			}
		};

		listbox.addEventListener("scroll", handleScroll);
		return () => listbox.removeEventListener("scroll", handleScroll);
	}, [options.length, largeList]);

	// Memoize final list
	const displayOptions = useMemo(() => {
		return largeList ? filteredOptions : options;
	}, [filteredOptions, options, largeList]);

	// Show more handler
	const handleShowAll = () => {
		setVisibleCount(options.length);
	};

	return (
		<FormControl className={className}>
			<Controller
				control={methods.control}
				name={name}
				render={({ field: { onChange, ref }, fieldState: { error } }) =>
					largeList ? (
						<Autocomplete
							disablePortal
							disableListWrap
							options={[
								...displayOptions,
								...(largeList &&
								visibleCount < options.length &&
								displayOptions.length > 10
									? [{ showMore: true }]
									: []),
							]}
							getOptionLabel={(option: any) =>
								option.showMore ? "" : getLabel(option)
							}
							value={value || null}
							onChange={(_, newValue) => {
								if (newValue && newValue.showMore) return;
								handleChange?.(newValue);
							}}
							onInputChange={(_, newInput) => setInputValue(newInput)}
							disabled={isDisabled}
							clearIcon={false}
							className="auto-complete-dialog"
							filterOptions={(x) => x} // disable internal filtering
							// ListboxProps={{
							// 	ref: listboxRef,
							// 	style: { maxHeight: 300, overflow: "auto" },
							// }}
							ListboxComponent={CustomListbox}
							PaperComponent={PaperComponent}
							isOptionEqualToValue={(option: IOption, value) =>
								option.value === value.value
							}
							renderOption={
								renderOption ||
								((props, option: any) =>
									option.showMore ? (
										<Box
											component="li"
											{...props}
											onClick={handleShowAll}
											sx={{
												textAlign: "center",
												color: "primary.main",
												cursor: "pointer",
												fontWeight: 500,
											}}
										>
											Show all ({options.length})
										</Box>
									) : (
										<li {...props}>{getLabel(option)}</li>
									))
							}
							renderInput={(params) => (
								<TextField
									{...params}
									label={label}
									inputRef={ref}
									error={!!error}
									autoComplete="off"
									helperText={error && !hideErrorMessage ? error.message : null}
									InputProps={{
										...params.InputProps,
										startAdornment: startAdornment,
										endAdornment: endAdornment
											? endAdornment
											: params.InputProps.endAdornment,
									}}
								/>
							)}
						/>
					) : (
						<Autocomplete
							disablePortal
							options={options.sort(
								(a, b) => -(b.groupBy || "").localeCompare(a.groupBy || "")
							)}
							disabled={isDisabled}
							clearIcon={false}
							className="auto-complete-dialog"
							value={value}
							groupBy={(option) => option.groupBy || ""}
							onChange={(
								event: React.SyntheticEvent<Element, Event>,
								newValue: IOption | null
							) => {
								onBeforeChange
									? onBeforeChange(newValue)
									: onChange(newValue ? newValue.value : null);
								if (!onBeforeChange && handleChange) handleChange(newValue);
							}}
							isOptionEqualToValue={(option: IOption, value) =>
								option.value === value.value
							}
							PaperComponent={PaperComponent}
							renderInput={(params) => (
								<TextField
									{...params}
									label={label}
									inputRef={ref}
									error={!!error}
									autoComplete="off"
									helperText={error && !hideErrorMessage ? error.message : null}
									InputProps={{
										...params.InputProps,
										startAdornment: startAdornment,
										endAdornment: endAdornment
											? endAdornment
											: params.InputProps.endAdornment,
									}}
								/>
							)}
							getOptionDisabled={(option) => !!option.disabledOptionTooltipText}
							renderOption={
								renderOption ||
								((props, option) => (
									<CustomTooltip
										key={option.value}
										tooltipStyle={{
											border: "1px solid #034f8b",
										}}
										tooltipProps={{
											title: option.disabledOptionTooltipText
												? option.disabledOptionTooltipText
												: "",
											placement: "right",
											children: (
												<span>
													<MenuItem
														{...props}
														disabled={!!option.disabledOptionTooltipText}
													>
														{isShowDescription && option.description
															? `${option.label} - ${option.description}`
															: option.label}
													</MenuItem>
												</span>
											),
										}}
									/>
								))
							}
						/>
					)
				}
			/>
		</FormControl>
	);
};
